import Vue from "vue";
import VueRouter, {
  type NavigationGuardNext,
  type Route,
  type RouteConfig,
} from "vue-router";
import DomainSelect from "../components/DomainSelect.vue";
import MessageEdit from "../components/MessageEdit.vue";
import MessageList from "../components/MessageList.vue";
import MessageSideMenuPanel from "../components/MessageSideMenuPanel.vue";
import NavigationBar from "../components/NavigationBar.vue";
import SearchResult from "../components/SearchResult.vue";
import TopicEdit from "../components/TopicEdit.vue";
import TopicList from "../components/TopicList.vue";
import TopicSideMenuPanel from "../components/TopicSideMenuPanel.vue";
import Login from "../views/Login.vue";
import Logout from "../views/Logout.vue";

Vue.use(VueRouter);

import { IconReactionType } from "@/API";
import { LoadingManager } from "@/components/loading/loading-manager";
import S3AccessUtility from "@/components/s3-acceess-utility";
import HistoryUtility from "@/history";
import type { Acl, Category, Comment, Message, Topic, User } from "@/model";
import AclManager from "@/model/acl-manager";
import { ReactionController } from "@/model/reaction-controller";
import type { AttachmentFileTypes } from "@/suppport-attachment-types";
import {
  type ISearchParam,
  useAppStore,
  useCategoryStore,
  useCommentStore,
  useDomainStore,
  useMessageStore,
  useTopicStore,
  useUserStore,
} from "../store";

// 検索結果表示用に整理した話題/投稿/コメントを統合したデータ構造
export type SearchResultItemData = {
  type: "topic" | "message" | "comment";
  updatedAt: Date;
  item: Message | Comment | Topic;
  text: string;
  topicTitle: string;
  messageTitle: string;
  pinned: boolean;
  star: boolean;
  link: string;
  acl?: Acl;
};

const normalizeDomainId = (domainId: string) => {
  if (!domainId) return; // route.params が一瞬 undefined になる遷移時に appStore を破壊しない
  const appStore = useAppStore();
  if (appStore.domainId !== domainId) {
    appStore.setDomainId(domainId);
    appStore.domainId = domainId;
  }
};
const normalizeTopicId = (topicId: string) => {
  if (!topicId) return;
  const appStore = useAppStore();
  if (appStore.topicId !== topicId) appStore.topicId = topicId;
};
const normalizeMessageId = (messageId: string) => {
  const appStore = useAppStore();
  if (appStore.messageId !== messageId) appStore.messageId = messageId;
};

// ★の点灯チェック(検索データ用)
const hasStar = (item: Topic | Message, user: User) =>
  item.star?.userList.some((u) => u.userId === user.directId);

const routes: Array<RouteConfig> = [
  {
    path: "/:domainId/new-topic",
    name: "new-topic",
    components: {
      default: TopicEdit,
    },
    props: {
      default: (route) => {
        normalizeDomainId(route.params.domainId);
        const domainId = useAppStore().domainId;
        const me = useUserStore().me as User;
        const categories = useCategoryStore().get(domainId) as Category[];
        // Direct組織の添付ファイル情報
        const allow_attachment_type = useDomainStore()
          .getAllowAttachmentType as AttachmentFileTypes;
        return {
          type: "create",
          categories,
          domainId,
          me,
          allow_attachment_type: allow_attachment_type,
          isMobile: true,
        };
      },
    },
  },
  {
    path: "/login",
    name: "login",
    component: Login,
  },
  {
    path: "/login-continue*",
    name: "login-continue",
    component: Login,
  },
  {
    path: "/logout",
    name: "logout",
    component: Logout,
  },
  {
    path: "/:domainId/:topicId/edit-topic",
    name: "edit-topic",
    components: {
      default: TopicEdit,
    },
    props: {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      default: (route) => {
        const domainId = route.params.domainId;
        const categories = useCategoryStore().get(domainId) as Category[];
        const topicId = route.params.topicId;
        const topics = useTopicStore().get(domainId) as Topic[] | undefined;
        const topic = topics?.find((t) => t.id === topicId);
        const me = useUserStore().me as User;
        // Direct組織の添付ファイル情報
        const allow_attachment_type = useDomainStore()
          .getAllowAttachmentType as AttachmentFileTypes;
        return {
          type: "edit",
          categories,
          topicId,
          domainId,
          title: topic?.title,
          desc: topic?.desc,
          me: me,
          thumb: topic?.icon,
          category: topic?.category,
          pinned: topic?.pinned,
          allow_attachment_type: allow_attachment_type,
          acl: topic?.acl,
          isMobile: true,
        };
      },
    },
  },
  {
    path: "/:domainId/:topicId/post-message",
    name: "post-message",
    components: {
      default: MessageEdit,
    },
    props: {
      default: (route) => {
        normalizeDomainId(route.params.domainId);
        const domainId = route.params.id || useAppStore().domainId;
        normalizeTopicId(route.params.topicId);
        const topicId = route.params.topicId || "error";
        // Direct組織の添付ファイル情報
        const allow_attachment_type = useDomainStore()
          .getAllowAttachmentType as AttachmentFileTypes;
        return {
          nav: true,
          type: "create",
          photos: [],
          domainId: domainId,
          topicId: topicId,
          allow_attachment_type: allow_attachment_type,
        };
      },
    },
  },
  {
    path: "/edit-message/:domainId/:topicId/:messageId",
    name: "edit-message",
    components: {
      default: MessageEdit,
    },
    props: {
      default: (route) => {
        const domainId = route.params.domainId || useAppStore().domainId;
        const topicId = route.params.topicId || "error";
        const messageId = route.params.messageId || "error";
        const messages = (useMessageStore().get(domainId, topicId) ||
          []) as Message[];
        const message = messages.find((m) => m.id === messageId);
        const topic = useAppStore().getTopic(domainId, topicId) as Topic;
        // Direct組織の添付ファイル情報
        const allow_attachment_type = useDomainStore()
          .getAllowAttachmentType as AttachmentFileTypes;
        return {
          nav: true,
          type: "edit",
          messageId: messageId,
          title: message?.title,
          message: message?.message,
          photos: message?.photos,
          domainId: domainId,
          topicId: topicId,
          allow_attachment_type: allow_attachment_type,
          acl: message?.acl,
          topicAcl: topic?.acl,
          talkId: message?.talkId,
          sendTalkOnCreate: message?.sendTalkOnCreate,
          sendTalkOnUpdate: message?.sendTalkOnUpdate,
        };
      },
    },
  },
  // {
  //     path: '/about',
  //     name: 'About',
  //     // route level code-splitting
  //     // this generates a separate chunk (about.[hash].js) for this route
  //     // which is lazy-loaded when the route is visited.
  //     component: () => import(/* webpackChunkName: "about" */ '../views/About.vue')
  // },

  // 組織選択
  {
    path: "/",
    name: "domain-select",
    components: {
      nav: NavigationBar,
      sidemenu: TopicSideMenuPanel, // サイドメニューは話題一覧と同じものを使用
      default: DomainSelect,
    },
    props: {
      nav: () => {
        const domainId = "";
        const domainName = "";
        const isMobile = useAppStore().isMobile as boolean;
        if (isMobile) {
          return {
            domainId: domainId,
            back: true,
            title: domainName,
            subTitle: "",
            appClose: true,
            search: true,
          };
        } else {
          return {
            domainId: domainId,
            brand: true,
            domains: true,
            apps: true,
            preset: true,
            search: true,
          };
        }
      },
      sidemenu: () => {
        return { domainId: "", categories: [], categoryFilter: [] };
      },
    },
  },

  // 話題一覧
  {
    path: "/:domainId",
    name: "topic-list",
    components: {
      nav: NavigationBar,
      sidemenu: TopicSideMenuPanel,
      default: TopicList,
      search: SearchResult,
    },
    props: {
      default: (route) => {
        normalizeDomainId(route.params.domainId);
        const domainId = useAppStore().domainId;
        const isDomainEnable = useDomainStore().isEnable(domainId);
        if (domainId && isDomainEnable) {
          const s3AccessUtility = S3AccessUtility.getInstance();
          s3AccessUtility.init();
          s3AccessUtility.setDomainId(domainId);
          // fetch依頼を出す
          const categories = useCategoryStore().get(domainId);
          const categoryFilter = useCategoryStore().filter(domainId);
          const topicList = useTopicStore().get(domainId);
          const users = useUserStore().getByDomainId(domainId) as User[];
          const me = useUserStore().me as User;
          const props = {
            domainId,
            categories,
            topicList,
            users,
            me,
            categoryFilter,
            fab: AclManager.createTopic(AclManager.store, domainId),
          };
          return props;
        } else {
          return {};
        }
      },
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      nav: (_route) => {
        const domainId = useAppStore().domainId;
        const isMobile = useAppStore().isMobile as boolean;
        const domainName = useDomainStore().getDomainName as string;
        if (isMobile) {
          return {
            domainId: domainId,
            back: true,
            title: domainName,
            subTitle: "",
            appClose: true,
            search: true,
          };
        } else {
          return {
            domainId: domainId,
            brand: true,
            domains: true,
            apps: true,
            preset: true,
            search: true,
          };
        }
      },
      search: (route) => {
        normalizeDomainId(route.params.domainId);
        const param = useAppStore().searchParam;
        const word = param.word;

        const allData: SearchResultItemData[] = [];
        const domainId = useAppStore().domainId;

        const users = useUserStore().getByDomainId(domainId) as User[];
        const me = useUserStore().me as User;

        const topics = (useTopicStore().get(domainId) || []) as Topic[];
        const ctl = new ReactionController();

        topics.forEach((topic) => {
          // Reactionマージ
          topic.star = ctl.getReaction(topic, IconReactionType.FAVORITE);

          const pinned = topic.pinned;
          const star = !!hasStar(topic, me);
          allData.push({
            type: "topic",
            updatedAt: topic.updatedAt,
            topicTitle: topic.title,
            messageTitle: "",
            text: topic.desc,
            item: topic,
            pinned: pinned,
            star: star,
            link: `/${domainId}/${topic.id}/`,
            acl: topic.acl,
          });
        });
        return {
          word: word,
          allData: allData,
          users: users,
          viewer: me,
          domainId: domainId,
        };
      },
      sidemenu: (_route) => {
        const domainId = useAppStore().domainId;
        const categories = useCategoryStore().get(domainId);
        const categoryFilter = useCategoryStore().filter(domainId);
        const isDomainEnable = useDomainStore().isEnable(domainId);
        if (!isDomainEnable) {
          return {
            domainId,
            categories: [],
            categoryFilter: [],
          };
        } else {
          return {
            domainId,
            categories,
            categoryFilter,
          };
        }
      },
    },
  },

  // 投稿一覧
  {
    path: "/:domainId/:topicId/:messageId?/:commentId?",
    name: "message-list",
    components: {
      nav: NavigationBar,
      sidemenu: MessageSideMenuPanel,
      default: MessageList,
      search: SearchResult,
    },
    props: {
      default: (route) => {
        normalizeDomainId(route.params.domainId);
        normalizeTopicId(route.params.topicId);
        const messageJumpId = route.params.messageId;
        const commentJumpId = route.params.commentId;

        const messageId = messageJumpId || "";
        normalizeMessageId(messageId);

        const domainId = useAppStore().domainId;
        const topicId = useAppStore().topicId;

        const s3AccessUtility = S3AccessUtility.getInstance();
        s3AccessUtility.init();
        s3AccessUtility.setDomainId(domainId);
        s3AccessUtility.setTopicId(topicId);

        // 話題取得
        const topic = useTopicStore().getOne(domainId, topicId) as
          | Topic
          | undefined;
        if (topic) {
          const categories = useCategoryStore().get(domainId) || [];
          const category = categories.find(
            (c: Category) => c.id === topic.category.id,
          );
          topic.category = category ? category : topic.category;
        }
        // 話題が削除済みか
        const deleted = !topic;
        // ゲスト許可
        const _allowGuest = topic ? topic.acl.guest.read : true;

        // ユーザー一覧、ログインユーザーの取得
        const users = useUserStore().getByDomainId(domainId) as User[];
        const me = useUserStore().me as User;

        // 投稿一覧の取得
        const storeMessages = (useMessageStore().get(domainId, topicId) ||
          []) as Message[];
        if (storeMessages.length > 0) {
          const ctl = new ReactionController();
          storeMessages.forEach((message) => {
            message.star = ctl.getReaction(message, IconReactionType.FAVORITE);
            message.like = ctl.getReaction(message, IconReactionType.LIKE);
          });
        }
        // topic の ★
        const ctl = new ReactionController();
        const topicStar = ctl.getReaction(topic, IconReactionType.FAVORITE);

        // Direct組織の添付ファイル情報
        const allow_attachment_type = useDomainStore()
          .getAllowAttachmentType as AttachmentFileTypes;
        const isDomainEnable = useDomainStore().isEnable(domainId);
        if (!isDomainEnable || !topic) {
          // 識別子は real のまま返す。空にすると $subscribe 経由の削除検知＆遷移先(replaceParam)が壊れる。
          // deleted: true で MessageList の isBlocked を立て、topicAcl/topicUpdatable 参照を回避する。
          return {
            domainId,
            topicId,
            icon: undefined,
            category: undefined,
            title: "",
            desc: "",
            pinned: false,
            topicOwner: "",
            topicAcl: undefined,
            star: undefined,
            deleted: true,
            messages: [],
            allow_attachment_type: undefined,
            fab: false,
            users: [],
            viewer: undefined,
          };
        } else {
          return {
            domainId: topic?.domainId,
            topicId: topic?.id,
            icon: topic?.icon,
            category: topic?.category,
            title: topic?.title,
            desc: topic?.desc,
            pinned: topic?.pinned,
            topicOwner: topic?.owner,
            topicAcl: topic?.acl,
            notification: topic?.notification,
            star: topicStar,
            deleted: deleted,
            messages: storeMessages,
            allow_attachment_type: allow_attachment_type,
            fab: AclManager.createMessage(AclManager.store, domainId),
            users: users,
            viewer: me,
            messageJump: messageJumpId,
            commentJump: commentJumpId,
          };
        }
      },
      nav: (route) => {
        const domainId = useAppStore().domainId;
        const topicId = route.params.topicId || "error";
        const topics = useTopicStore().get(domainId) as Topic[];
        const topic = topics ? topics.find((t) => t.id === topicId) : undefined;
        const isMobile = useAppStore().isMobile as boolean;
        if (isMobile) {
          return {
            domainId: domainId,
            back: true,
            title: topic?.title,
            appClose: true,
            search: true,
          };
        } else {
          return {
            domainId: domainId,
            brand: true,
            domains: true,
            apps: true,
            back: true,
            title: topic?.title,
            preset: true,
            search: true,
          };
        }
      },
      search: (route) => {
        normalizeDomainId(route.params.domainId);
        normalizeTopicId(route.params.topicId);

        const param = useAppStore().searchParam;
        const word = param.word;

        const allData: SearchResultItemData[] = [];
        const domainId = useAppStore().domainId;
        const topicId = route.params.topicId || "error";

        const users = useUserStore().getByDomainId(domainId) as User[];
        const me = useUserStore().me as User;

        const topics = useTopicStore().get(domainId) as Topic[];
        const topic = topics ? topics.find((t) => t.id === topicId) : undefined;
        const topicTitle = topic ? topic.title : "";

        const storeMessages = (useMessageStore().get(domainId, topicId) ||
          []) as Message[];
        const ctl = new ReactionController();
        storeMessages.forEach((m: Message) => {
          const comments = useCommentStore().get(m.topicId, m.id) as Comment[];
          // コメントの配列をmessage.commentsへマージ
          m.comments = comments;
          // Reactionマージ
          m.star = ctl.getReaction(m, IconReactionType.FAVORITE);
          m.like = ctl.getReaction(m, IconReactionType.LIKE);

          if (!m.deleted) {
            const pinned = m.pinned;
            const star = !!hasStar(m, me);
            allData.push({
              type: "message",
              updatedAt: m.updatedAt,
              topicTitle: topicTitle,
              messageTitle: m.title,
              text: m.message,
              item: m,
              pinned: pinned,
              star: star,
              link: `/${domainId}/${topicId}/${m.id}`,
              acl: m.acl,
            });
          }
          if (!comments || m.deleted) return; // コメントがない場合 もしくは 親投稿が削除されている場合 コメントは検索から外す
          comments.forEach((c: Comment) => {
            // Reactionマージ
            c.like = ctl.getReaction(c, IconReactionType.LIKE);
            if (!c.deleted) {
              allData.push({
                type: "comment",
                updatedAt: c.updatedAt,
                topicTitle: topicTitle,
                messageTitle: m.title,
                text: c.message,
                item: c,
                pinned: false,
                star: false,
                link: `/${domainId}/${topicId}/${m.id}/${c.id}`,
              });
            }
          });
        });
        return {
          word: word,
          allData: allData,
          users: users,
          viewer: me,
          domainId: domainId,
          topicId: topicId,
        };
      },
      sidemenu: (route) => {
        normalizeDomainId(route.params.domainId);
        const domainId = useAppStore().domainId;
        normalizeTopicId(route.params.topicId);
        const topicId = useAppStore().topicId;
        const categories = useCategoryStore().get(domainId);
        const categoryFilter = useCategoryStore().filter(domainId);
        const topicList = useTopicStore().get(domainId) as Topic[];

        // ユーザー一覧、ログインユーザーの取得
        const users = useUserStore().getByDomainId(domainId) as User[];
        const me = useUserStore().me as User;
        const isDomainEnable = useDomainStore().isEnable(domainId);
        if (!isDomainEnable) {
          return {};
        } else {
          return {
            domainId,
            categories,
            topicList,
            topicId,
            users,
            me,
            categoryFilter,
          };
        }
      },
    },
  },
];

const router = new VueRouter({
  mode: "history",
  base: import.meta.env.BASE_URL,
  routes,
});

router.beforeEach((to: Route, _from: Route, next: NavigationGuardNext) => {
  if (to.name === "topic-list" || to.name === "message-list") {
    // 検索ワードをセット（NOTE: 初期データ未ロード時はロード後にセットされる）
    const word = useAppStore().searchParam.word;
    const searchQuery = (to.query.search as string) || "";
    if (word !== searchQuery && LoadingManager.isEndInitialLoad()) {
      const searchParam: ISearchParam = { word: searchQuery };
      router.app.$emit("on-set-search-param", searchParam);
    }
  }
  next();
});

router.afterEach((_to: Route, from: Route) => {
  HistoryUtility.afterEach(from);
});

export default router;
